import {TranslationObject} from '@colonist/i18n'
import {UIRoomSliderController} from '../../ui_html/room/UIRoomSliderController'
import {ReplayHighlightData, UIReplaySliderHighlight} from './UIReplaySliderHighlight'
import {createDivIn} from '../../ui_utils/DomUtils'
import {MathUtils} from '../../../../../shared/utils/MathUtils'

export class UIReplaySlider extends UIRoomSliderController {
    private readonly highlights: UIReplaySliderHighlight[] = []
    private readonly highlightsContainer: HTMLDivElement
    private readonly onInputActionIndex: (actionIndex: number) => void
    readonly numberOfActions: number

    private static readonly RANGE_MAX = 1000

    constructor(container: HTMLDivElement, title: TranslationObject, initialAction: number, numberOfActions: number, onInputActionIndex: (value: number) => void) {
        super(container, title, -1, -1, UIReplaySlider.RANGE_MAX, 1)

        this.numberOfActions = numberOfActions
        this.onInputActionIndex = onInputActionIndex

        this.highlightsContainer = createDivIn(this.sliderContainer, 'replay-slider-highlights-container')
        this.highlightsContainer.style.gridTemplateColumns = `repeat(${this.numberOfActions}, 1fr)`

        this.headerContainer.style.display = 'none'
        this.setOnInput((value) => {
            const {minValue, minAction, maxValue, maxAction} = this.getSurroundingHighlightValues(value)
            const actionIndex = Math.round(MathUtils.lerp(minValue, maxValue, minAction, maxAction, value))

            this.onInputActionIndex(actionIndex)
            this.setAction(actionIndex)
        })
        this.setHighlightsVisible(true)
        this.setAction(initialAction)
    }

    addHighlight(data: ReplayHighlightData) {
        const shiftedActionIndex = data.actionIndex + 1

        const newHighlight = new UIReplaySliderHighlight(this.highlightsContainer, data)
        this.highlights.push(newHighlight)

        newHighlight.container.onclick = () => this.onInputActionIndex(shiftedActionIndex)
        newHighlight.container.addEventListener('mouseover', () => this.setHighlightFocus(newHighlight))
        newHighlight.container.addEventListener('mouseout', () => this.resetHighlightFocus())
    }

    setHighlightsVisible(isVisible: boolean): void {
        this.highlightsContainer.hidden = !isVisible
    }

    setAction(action: number) {
        const {minAction, maxAction} = this.getSurroundingHighlightActions(action)
        const rangeValueMin = this.getRangeValueForHighlight(minAction) ?? 0
        const rangeValueMax = this.getRangeValueForHighlight(maxAction) ?? UIReplaySlider.RANGE_MAX
        const rangeValue = MathUtils.lerp(minAction, maxAction, rangeValueMin, rangeValueMax, action)
        if(rangeValue === this.slider.valueAsNumber) return

        this.setValue(rangeValue)
    }

    private setHighlightFocus(except: UIReplaySliderHighlight) {
        for(const highlight of this.highlights) {
            if(highlight == except) highlight.container.style.opacity = '1'
            else highlight.container.style.opacity = '0.3'
        }
    }
    private resetHighlightFocus() {
        for(const highlight of this.highlights) {
            highlight.container.style.opacity = '1'
        }
    }

    private getSurroundingHighlightActions(action: number): {minAction: number, maxAction: number} {
        const initialActionIndex = -1
        let minAction = initialActionIndex
        let maxAction = this.numberOfActions + initialActionIndex - 1
        if(this.highlightsContainer.hidden) return {minAction: minAction, maxAction: maxAction}

        for(const highlight of this.highlights) {
            const highlightAction = highlight.data.actionIndex

            if(highlightAction <= action && minAction < highlightAction) minAction = highlightAction
            if(action < highlightAction && highlightAction < maxAction) maxAction = highlightAction
        }

        return {minAction: minAction, maxAction: maxAction}
    }

    private getRangeValueForHighlight(actionIndex: number): number | undefined {
        const sliderHighlight = this.highlights.find((highlight) => highlight.data.actionIndex == actionIndex)
        if(!sliderHighlight) return undefined

        const replaySliderThumbDiameter = 15 // same as $replay-slider-thumb-diameter

        const sliderHighlightMidpoint = sliderHighlight.container.offsetLeft + sliderHighlight.container.offsetWidth / 2 - replaySliderThumbDiameter / 2
        const sliderAvailableMovement = this.sliderContainer.clientWidth - replaySliderThumbDiameter
        return Math.round(sliderHighlightMidpoint / sliderAvailableMovement * UIReplaySlider.RANGE_MAX)
    }

    private getSurroundingHighlightValues(value: number): {minValue: number, maxValue: number, minAction: number, maxAction: number} {
        const initialActionIndex = -1
        let minValue = initialActionIndex
        let minAction = initialActionIndex
        let maxValue = UIReplaySlider.RANGE_MAX
        let maxAction = this.numberOfActions + initialActionIndex - 1
        for(const highlight of this.highlights) {
            const highlightValue = this.getRangeValueForHighlight(highlight.data.actionIndex)
            if(highlightValue === undefined) continue

            if(highlightValue <= value && minValue < highlightValue) {
                minValue = highlightValue
                minAction = highlight.data.actionIndex
            }
            if(value < highlightValue && highlightValue < maxValue) {
                maxValue = highlightValue
                maxAction = highlight.data.actionIndex
            }
        }

        return {minValue: minValue, maxValue: maxValue, minAction: minAction, maxAction: maxAction}
    }
}
